// SEO Configuration for BagpipeBlasphemy
// Centralized metadata management for all production routes

export const seoConfig = {
  // HOME PAGE (Priority: 1.0)
  '/': {
    title: 'Master the Highland Bagpipes — Faster, Smarter, and More Enjoyably',
    description: 'Transform Highland bagpipe practice with accurate, real-time feedback games. Optimized for digital practice chanters on line-in or USB; acoustic works after a quick calibration.',
    keywords: 'bagpipe practice, highland bagpipe exercises, bagpipe games, scottish music practice, pipe band training, bagpipe tuner, bagpipe lessons, band finder and sheet music library',
    ogImage: '/images/home-og.jpg'
  },

  // GAMES (Priority: 0.9)
  '/games': {
    title: 'Games - Note Blaster, Note Battle, Simon & Echo',
    description: 'Choose Note Blaster, Note Battle, Melodic Simon, or Phrase Echo after calibrating your digital practice chanter.',
    keywords: 'bagpipe games, note blaster, note battle, bagpipe battle, melodic simon, phrase echo, ear training, digital practice chanter',
    ogImage: '/images/blaster-og.jpg'
  },

  '/blaster': {
    title: 'Note Blaster - Bagpipe Practice Game',
    description: 'Improve your note recognition and bagpipe skills with Note Blaster. Interactive practice game for Highland bagpipe players with real-time feedback.',
    keywords: 'bagpipe game, note recognition, music practice game, bagpipe exercises, highland bagpipe practice, note blaster',
    ogImage: '/images/blaster-og.jpg'
  },

  '/note-battle': {
    title: 'Note Battle - Bagpipe Stave Practice Game',
    description: 'Scroll-hit stave practice for melody and grace notes. Real-time pitch feedback with your calibrated digital practice chanter.',
    keywords: 'note battle, bagpipe battle, bagpipe fingering game, highland bagpipe challenge, stave practice',
    ogImage: '/images/battle-og.jpg'
  },

  // Legacy path — App redirects to /note-battle; keep metadata for bookmarks/crawlers
  '/battle': {
    title: 'Note Battle - Bagpipe Stave Practice Game',
    description: 'Scroll-hit stave practice for melody and grace notes. Real-time pitch feedback with your calibrated digital practice chanter.',
    keywords: 'note battle, bagpipe battle, bagpipe fingering game, highland bagpipe challenge',
    ogImage: '/images/battle-og.jpg',
    canonical: '/note-battle',
  },

  '/simon': {
    title: 'Melodic Simon - Bagpipe Phrase Memory Game',
    description: 'Watch a growing phrase from a real Highland tune, then play it back on your digital practice chanter. Pitch-change scoring works with continuous-sounding chanters.',
    keywords: 'melodic simon, bagpipe memory game, bagpipe phrase practice, digital practice chanter, highland bagpipe game',
    ogImage: '/images/blaster-og.jpg'
  },

  '/echo': {
    title: 'Phrase Echo - Bagpipe Ear Training Game',
    description: 'Call-and-response ear training: listen to a short phrase from a real Highland tune, then echo it on your digital practice chanter. Note names stay hidden until you miss.',
    keywords: 'phrase echo, bagpipe ear training, call and response, digital practice chanter, highland bagpipe game',
    ogImage: '/images/blaster-og.jpg'
  },

  // INSTRUCTION (Priority: 0.9)
  '/instruction': {
    title: 'Highland Bagpipe Instruction & Tutorials',
    description: 'Find Highland bagpipe instruction near you. Georeferenced map to find tutors and instructors.',
    keywords: 'bagpipe instruction, highland bagpipe lessons, bagpipe tutorials, scottish music lessons, pipe band instruction, bagpipe techniques',
    ogImage: '/images/instruction-og.jpg'
  },

  // TOOLS (Priority: 0.8)
  '/chantersync': {
    title: 'Manage Chanters - Digital Chanter Setup',
    description: 'Add or manage digital practice chanter profiles so the app recognizes the notes your chanter produces.',
    keywords: 'manage chanters, add chanter, bagpipe practice tool, highland bagpipe exercises, bagpipe technique, pipe practice, chanter training',
    ogImage: '/images/chantersync-og.jpg',
    canonical: '/chanter-sync'
  },

  '/tuner': {
    title: 'Professional Bagpipe Tuner',
    description: 'Precision online bagpipe tuner for Highland bagpipes and chanters. Simultaneous bagpipe drone tuning. Achieve perfect pitch with our professional tuning tools and real-time feedback.',
    keywords: 'bagpipe tuner, chanter tuner, highland bagpipe tuning, pipe tuning, bagpipe pitch, professional tuner',
    ogImage: '/images/tuner-og.jpg'
  },

  '/metronome': {
    title: 'Bagpipe Metronome & Timing Tool',
    description: 'Professional metronome designed for bagpipe practice. Improve your timing and rhythm with customizable tempo settings for Highland bagpipe music.',
    keywords: 'bagpipe metronome, pipe band metronome, highland bagpipe timing, scottish music tempo, bagpipe rhythm practice',
    ogImage: '/images/metronome-og.jpg'
  },

  '/flashcards': {
    title: 'Bagpipe Fingering Flashcards',
    description: 'Master Highland bagpipe fingerings with interactive flashcards. Practice note recognition and finger patterns for perfect bagpipe technique.',
    keywords: 'bagpipe flashcards, fingering practice, highland bagpipe fingerings, note recognition, bagpipe exercises',
    ogImage: '/images/flashcards-og.jpg'
  },

  '/basic-notes': {
    title: 'Basic Bagpipe Notes Practice',
    description: 'Learn and practice basic Highland bagpipe notes. Perfect for beginners starting their bagpipe journey with fundamental note recognition and fingering.',
    keywords: 'basic bagpipe notes, beginner bagpipe practice, highland bagpipe fundamentals, bagpipe note learning',
    ogImage: '/images/basic-notes-og.jpg'
  },

  '/gracenotes': {
    title: 'Highland Bagpipe Gracenotes Practice',
    description: 'Master Highland bagpipe gracenotes with interactive exercises. Learn traditional Scottish gracenote patterns and embellishments.',
    keywords: 'bagpipe gracenotes, highland bagpipe embellishments, scottish gracenotes, bagpipe ornamentation, traditional bagpipe techniques',
    ogImage: '/images/gracenotes-og.jpg'
  },

  '/chanter-render': {
    title: 'Interactive Bagpipe Chanter View',
    description: 'Visual bagpipe chanter interface showing finger positions and note relationships. Perfect learning tool for Highland bagpipe students.',
    keywords: 'bagpipe chanter, visual bagpipe learning, chanter fingerings, interactive bagpipe tool',
    ogImage: '/images/chanter-render-og.jpg'
  },

  // MEDIUM PRIORITY (0.6-0.7)
  '/chanter-test': {
    title: 'Bagpipe Chanter Testing Tool',
    description: 'Test your bagpipe chanter calibration with precision. Ensure optimal performance of our tools.',
    keywords: 'chanter testing, bagpipe chanter calibration, chanter setup, lesson preparation',
    ogImage: '/images/chanter-test-og.jpg'
  },

  '/aisheetnovel': {
    title: 'AI-Powered Bagpipe Sheet Music',
    description: 'Generate and practice Highland bagpipe sheet music with AI assistance. Create custom tunes from traditional Scottish to crazy new ones.',
    keywords: 'bagpipe sheet music, AI music generation, highland bagpipe scores, scottish music sheets',
    ogImage: '/images/ai-sheet-og.jpg'
  },

  '/leader-board': {
    title: 'Bagpipe Practice Leaderboard',
    description: 'Track your progress and compete with other Highland bagpipe players. View high scores and achievements across all practice games.',
    keywords: 'bagpipe leaderboard, practice tracking, bagpipe competition, player rankings',
    ogImage: '/images/leaderboard-og.jpg'
  },

  '/resources': {
    title: 'Highland Bagpipe Resources',
    description: 'In-app entry to curated bagpipe resource directories. Indexable lists live at /learn/resources (electronic bagpipes, gear shops, YouTube channels, teachers).',
    keywords: 'bagpipe resources, highland bagpipe tutorials, youtube bagpipe lessons, bagpipe gear, electronic bagpipes, scottish music resources, pipe band resources',
    ogImage: '/images/resources-og.jpg'
  },

  '/microphone-help': {
    title: 'Microphone Setup for Bagpipe Practice',
    description: 'Complete guide to setting up your microphone for optimal bagpipe practice with our digital tools and games.',
    keywords: 'bagpipe microphone setup, mic configuration, bagpipe recording, practice setup',
    ogImage: '/images/mic-help-og.jpg'
  },

  // LOWER PRIORITY (0.3-0.5)
  '/contact': {
    title: 'Contact BagpipeBlasphemy',
    description: 'Get in touch with the BagpipeBlasphemy team. Questions, feedback, or support for your Highland bagpipe practice journey.',
    keywords: 'contact bagpipe blasphemy, bagpipe support, highland bagpipe help',
    ogImage: '/images/contact-og.jpg'
  },

  '/about-us': {
    title: 'About BagpipeBlasphemy',
    description: 'Learn about BagpipeBlasphemy - the premier platform for Highland bagpipe practice tools, games, and learning resources.',
    keywords: 'about bagpipe blasphemy, highland bagpipe platform, scottish music technology',
    ogImage: '/images/about-og.jpg'
  },

  '/feature-request': {
    title: 'Request Features - BagpipeBlasphemy',
    description: 'Suggest new features and improvements for BagpipeBlasphemy. Help us build better Highland bagpipe practice tools.',
    keywords: 'feature request, bagpipe tool suggestions, platform feedback',
    ogImage: '/images/feature-request-og.jpg'
  },

  '/settings': {
    title: 'Settings - BagpipeBlasphemy',
    description: 'Configure your BagpipeBlasphemy experience. Customize practice settings, audio preferences, and tool configurations.',
    keywords: 'bagpipe practice settings, tool configuration, audio settings',
    ogImage: '/images/settings-og.jpg'
  },

  '/auth': {
    title: 'Login - BagpipeBlasphemy',
    description: 'Sign in to BagpipeBlasphemy to track your progress, save high scores, and access personalized Highland bagpipe practice features.',
    keywords: 'bagpipe practice login, user account, progress tracking',
    ogImage: '/images/auth-og.jpg'
  },

  // BAND DISCOVERY & NETWORKING (New Major Feature)
  '/bands': {
    title: 'Find Highland Bagpipe Bands Near You',
    description: 'Discover Highland bagpipe bands in your area. Connect with competition, performance, and community bands. Find opportunities for auditions, performances, and band membership.',
    keywords: 'bagpipe bands, highland bagpipe bands, pipe bands near me, bagpipe band auditions, scottish pipe bands, competition bands, performance bands',
    ogImage: '/images/bands-og.jpg'
  },

  '/band-registration': {
    title: 'Register Your Highland Bagpipe Band',
    description: 'Register your Highland bagpipe band to connect with potential members. List your band for recruitment, performances, and community visibility.',
    keywords: 'register bagpipe band, band registration, pipe band recruitment, highland bagpipe band listing, band profile creation',
    ogImage: '/images/band-registration-og.jpg'
  },

  '/band-profile': {
    title: 'Highland Bagpipe Band Profile Management',
    description: 'Manage your Highland bagpipe band profile. Update band information, recruitment status, and connect with potential members.',
    keywords: 'band profile, pipe band management, band recruitment, bagpipe band profile, highland bagpipe band',
    ogImage: '/images/band-profile-og.jpg'
  },

  // SHEET MUSIC & SHARING
  '/sheet-share': {
    title: 'Share Highland Bagpipe Sheet Music',
    description: 'Share and discover Highland bagpipe sheet music. Connect with other pipers and access a community library of traditional and modern bagpipe tunes.',
    keywords: 'bagpipe sheet music sharing, highland bagpipe scores, scottish music sheets, pipe band music, bagpipe tune library',
    ogImage: '/images/sheet-share-og.jpg'
  },

  // USER MANAGEMENT
  '/user-profile': {
    title: 'User Profile - BagpipeBlasphemy',
    description: 'Manage your BagpipeBlasphemy user profile. Track your Highland bagpipe learning progress, achievements, and connect with the piping community.',
    keywords: 'user profile, bagpipe progress tracking, highland bagpipe learning, piper profile, bagpipe achievements',
    ogImage: '/images/user-profile-og.jpg'
  }
};

// Default SEO settings for any routes not specifically configured
export const defaultSEO = {
  title: 'BagpipeBlasphemy - Highland Bagpipe Practice Platform',
  description: 'Interactive Highland bagpipe practice platform with games, tuning tools, and exercises for pipers of all skill levels. Master Scottish music with technology.',
  keywords: 'bagpipes, highland bagpipes, bagpipe practice, scottish music, pipe band, bagpipe games, bagpipe tuner',
  ogImage: '/images/default-og.jpg'
};

// Schema.org structured data templates
export const schemaTemplates = {
  // Home page - WebApplication schema
  home: {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "BagpipeBlasphemy",
    "description": "Interactive Highland bagpipe practice platform with games, tuning tools, and exercises",
    "url": "https://bagpipeblasphemy.com",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Note Blaster Game",
      "Note Battle",
      "Professional Tuner",
      "Manage Chanters / Digital Chanter Setup",
      "Interactive Flashcards"
    ]
  },

  // Educational content schema
  instruction: {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Highland Bagpipe Instruction",
    "description": "Comprehensive Highland bagpipe instruction and practice exercises",
    "provider": {
      "@type": "Organization",
      "name": "BagpipeBlasphemy"
    },
    "courseMode": "online",
    "educationalLevel": "beginner to advanced",
    "about": {
      "@type": "Thing",
      "name": "Highland Bagpipes"
    }
  },

  // Software application schema for tools
  tool: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "applicationCategory": "MusicApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  },

  // Game schema
  game: {
    "@context": "https://schema.org",
    "@type": "Game",
    "genre": "Educational Music Game",
    "gamePlatform": "Web Browser",
    "applicationCategory": "GameApplication"
  },

  // Band discovery schema
  bands: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Highland Bagpipe Band Directory",
    "description": "Discover Highland bagpipe bands in your area",
    "about": {
      "@type": "Thing",
      "name": "Highland Bagpipe Bands"
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": "Highland Bagpipe Bands",
      "description": "Directory of Highland bagpipe bands for performances, competitions, and recruitment"
    }
  },

  // Band registration schema
  bandRegistration: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Register Highland Bagpipe Band",
    "description": "Register your Highland bagpipe band for community visibility and member recruitment",
    "about": {
      "@type": "Thing",
      "name": "Highland Bagpipe Band Registration"
    }
  }
}; 